import { useCustom } from '@refinedev/core';
import { Spin, Table } from 'antd';
import { Cpu, DollarSign, Bot } from 'lucide-react';
import type { FC } from 'react';
import StatCard from '@/components/common/StatCard';

interface AiUsageStats {
  totalTokensUsed: number;
  totalCost: number;
  modelsUsage: Array<{
    model: string;
    tokensUsed: number;
    cost: number;
    requestCount: number;
  }>;
  dailyUsage: Array<{
    date: string;
    tokensUsed: number;
    cost: number;
  }>;
}

const TechDashboardPage: FC = () => {
  const { data, isLoading, isError } = useCustom<{ data: AiUsageStats }>({
    url: '/api/v1/admin/stats/ai-usage',
    method: 'get',
  });

  const stats = data?.data?.data;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="fc-card p-6 border-error-500">
        <p className="text-error-600 dark:text-error-500 font-medium">
          Impossible de charger les statistiques IA.
        </p>
      </div>
    );
  }

  const modelColumns = [
    { title: 'Modèle', dataIndex: 'model', key: 'model' },
    { title: 'Tokens', dataIndex: 'tokensUsed', key: 'tokensUsed', render: (v: number) => v?.toLocaleString('fr-FR') ?? '—' },
    { title: 'Coût', dataIndex: 'cost', key: 'cost', render: (v: number) => (v != null ? `${v.toFixed(2)} €` : '—') },
    { title: 'Requêtes', dataIndex: 'requestCount', key: 'requestCount' },
  ];

  return (
    <div className="space-y-6">
      <div className="fc-gradient-header rounded-2xl p-6 text-white">
        <h1 className="text-xl font-bold">Consommation IA</h1>
        <p className="mt-1 text-sm text-blue-100">Monitoring tokens Claude & Whisper</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title="Tokens totaux" value={stats?.totalTokensUsed?.toLocaleString('fr-FR')} icon={Cpu} iconColor="text-blue-600 dark:text-blue-400" iconBg="bg-blue-50 dark:bg-blue-900/30" />
        <StatCard title="Coût total" value={stats?.totalCost != null ? `${stats.totalCost.toFixed(2)} €` : undefined} icon={DollarSign} iconColor="text-emerald-600 dark:text-emerald-400" iconBg="bg-emerald-50 dark:bg-emerald-900/30" />
        <StatCard title="Modèles" value={stats?.modelsUsage?.length ?? 0} icon={Bot} iconColor="text-violet-600 dark:text-violet-400" iconBg="bg-violet-50 dark:bg-violet-900/30" />
      </div>

      {stats?.modelsUsage && stats.modelsUsage.length > 0 && (
        <div className="fc-card p-0 overflow-hidden">
          <div className="px-5 py-4 border-b border-border dark:border-border-dark">
            <h3 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
              Répartition par modèle
            </h3>
          </div>
          <Table
            dataSource={stats.modelsUsage}
            columns={modelColumns}
            rowKey="model"
            pagination={false}
            size="small"
          />
        </div>
      )}
    </div>
  );
};

export default TechDashboardPage;
